import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useLanguage } from '../LanguageContext';
import './AccountPages.css';

const ESCROW_SPLIT = {
  serviceFeeRate: 5,
  sellerShareRate: 95,
};

function yuan(cents) {
  return `¥${((Number(cents) || 0) / 100).toFixed(2)}`;
}

function formatRate(rate) {
  const value = Number(rate);
  if (!Number.isFinite(value)) return '0';
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}

export default function Checkout() {
  const { language } = useLanguage();
  const text = language === 'zh' ? {
    title: '结算',
    subtitle: '确认订单信息并支付到平台托管，验收完成后再结算给技术员。',
    doneTitle: '托管付款已创建',
    doneText: id => `申请编号 #${id} 已记录。请选择支付宝完成自动付款。`,
    paidTitle: '支付已完成',
    paidText: id => `申请编号 #${id} 已完成支付，平台会继续处理托管状态。`,
    backWallet: '返回钱包',
    serviceName: '需求或服务名称',
    servicePlaceholder: '如：React 管理后台开发',
    amount: '托管成交额（元）',
    requirements: '项目需求',
    requirementsPlaceholder: '说明目标、交付物、验收标准和修改范围',
    deadline: '交付截止日期',
    attachments: '需求附件',
    attachmentsHint: '最多上传 5 个文件',
    description: '补充说明',
    descriptionPlaceholder: '补充沟通方式、取消规则或其他约定',
    saveDetails: '保存订单信息',
    savingDetails: '保存中...',
    detailsSaved: '订单信息已保存',
    removeAttachment: '移除',
    summaryTitle: '订单摘要',
    packageAmount: '套餐金额',
    serviceFee: rate => `平台服务费（${formatRate(rate)}%）`,
    serviceFeeEn: rate => `Service fee (${formatRate(rate)}%)`,
    sellerReceives: rate => `技术员结算（${formatRate(rate)}%）`,
    sellerReceivesEn: rate => `Seller receives (${formatRate(rate)}%)`,
    totalEscrow: '买家支付合计',
    escrowNote: (feeRate, sellerRate) => `资金先由 Oblitrok 托管。客户确认收货后，平台按当前订单快照结算：平台服务费 ${formatRate(feeRate)}%，技术员入账 ${formatRate(sellerRate)}%。`,
    beforePayTitle: '支付前请确认',
    beforePayText: '请先确认服务范围、交付时间、修改次数和验收标准。支付成功后订单进入托管流程。',
    submitting: '创建中...',
    submit: '创建托管付款',
    alipay: '使用支付宝自动付款',
    alipayAuto: '支付宝自动收款',
    alipayPaused: '支付宝未启用',
    alipayNotice: '点击后会跳转到支付宝官方收银台。付款成功后，平台会通过异步通知自动确认到账并进入托管。',
    alipayNotReady: '支付宝尚未配置，请联系平台管理员开通后再付款。',
    alipayCreating: '正在创建支付宝付款...',
    alipayOpened: '已打开支付宝收银台，请在支付宝页面完成付款。付款成功后状态会自动更新。',
    alipayFormMissing: '支付宝收银台表单生成失败',
    error: '创建托管付款申请失败',
    alipayError: '支付宝自动付款失败',
  } : {
    title: 'Checkout',
    subtitle: 'Confirm the order and pay into escrow. Funds are released after delivery is accepted.',
    doneTitle: 'Escrow payment created',
    doneText: id => `Request #${id} has been recorded. Choose Alipay to complete automatic payment.`,
    paidTitle: 'Payment completed',
    paidText: id => `Request #${id} has been paid. The platform will continue processing escrow status.`,
    backWallet: 'Back to wallet',
    serviceName: 'Task or service name',
    servicePlaceholder: 'Example: React admin dashboard development',
    amount: 'Escrow amount',
    requirements: 'Project requirements',
    requirementsPlaceholder: 'Describe goals, deliverables, acceptance criteria, and revision scope',
    deadline: 'Delivery deadline',
    attachments: 'Requirement attachments',
    attachmentsHint: 'Up to 5 files',
    description: 'Additional note',
    descriptionPlaceholder: 'Add communication, cancellation, or other notes',
    saveDetails: 'Save order details',
    savingDetails: 'Saving...',
    detailsSaved: 'Order details saved',
    removeAttachment: 'Remove',
    summaryTitle: 'Order summary',
    packageAmount: 'Package amount',
    serviceFee: rate => `Service fee (${formatRate(rate)}%)`,
    serviceFeeEn: rate => `Service fee (${formatRate(rate)}%)`,
    sellerReceives: rate => `Seller receives (${formatRate(rate)}%)`,
    sellerReceivesEn: rate => `Seller receives (${formatRate(rate)}%)`,
    totalEscrow: 'Buyer total',
    escrowNote: (feeRate, sellerRate) => `Funds are held by Oblitrok. After the buyer accepts delivery, the order snapshot settles ${formatRate(feeRate)}% to the platform and ${formatRate(sellerRate)}% to the provider.`,
    beforePayTitle: 'Confirm before payment',
    beforePayText: 'Confirm scope, delivery time, revision terms, and acceptance criteria before paying.',
    submitting: 'Creating...',
    submit: 'Create escrow payment',
    alipay: 'Pay with Alipay',
    alipayAuto: 'Alipay automatic payment',
    alipayPaused: 'Alipay paused',
    alipayNotice: 'You will be redirected to Alipay hosted checkout. After payment succeeds, the platform confirms escrow automatically through async notification.',
    alipayNotReady: 'Alipay is not configured. Contact the platform admin before paying.',
    alipayCreating: 'Creating Alipay checkout...',
    alipayOpened: 'Alipay checkout opened. Complete payment there; the platform will update automatically after payment succeeds.',
    alipayFormMissing: 'Alipay checkout form was not generated',
    error: 'Failed to create escrow payment request',
    alipayError: 'Alipay payment failed',
  };

  const [searchParams] = useSearchParams();
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    title: searchParams.get('title') || '',
    amount: searchParams.get('amount') || '',
    requirements: searchParams.get('requirements') || '',
    deadline: searchParams.get('deadline') || '',
    description: searchParams.get('description') || '',
  });
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [savedAttachments, setSavedAttachments] = useState([]);
  const [requestId, setRequestId] = useState(null);
  const [escrowSplit, setEscrowSplit] = useState(ESCROW_SPLIT);
  const [orderSummary, setOrderSummary] = useState(null);
  const [alipayConfig, setAlipayConfig] = useState({ enabled: false, configured: false, currency: 'CNY', mode: 'sandbox' });
  const [paymentStatus, setPaymentStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const existingRequestId = searchParams.get('requestId');
  const displayRequestId = requestId || searchParams.get('paymentNo') || '-';
  const amountCents = Math.max(0, Math.round(Number(form.amount || 0) * 100));
  const packageAmountCents = orderSummary?.packageAmountCents ?? amountCents;
  const serviceFeeCents = orderSummary?.serviceFeeCents
    ?? Math.round((amountCents * Number(escrowSplit.serviceFeeRate || 0)) / 100);
  const sellerReceivesCents = orderSummary?.sellerReceivesCents
    ?? Math.max(0, amountCents - serviceFeeCents);
  const totalEscrowCents = orderSummary?.totalCents ?? amountCents;

  useEffect(() => {
    api.get('/payments/alipay/config').then(res => setAlipayConfig(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!existingRequestId) return;
    setError('');
    api.get(`/payments/checkout-requests/${existingRequestId}`)
      .then(res => {
        const request = res.data.request;
        setRequestId(request.id);
        setForm({
          title: request.title || '',
          amount: request.amount ? (Number(request.amount) / 100).toFixed(2) : '',
          requirements: request.requirements || '',
          deadline: request.deadline || '',
          description: request.description || '',
        });
        setSavedAttachments(request.attachments || []);
        setEscrowSplit({
          serviceFeeRate: Number(request.platform_fee_rate ?? ESCROW_SPLIT.serviceFeeRate),
          sellerShareRate: Number(request.seller_share_rate ?? ESCROW_SPLIT.sellerShareRate),
        });
        setOrderSummary(request.service_booking_id ? {
          packageAmountCents: Number(request.seller_amount ?? request.amount ?? 0),
          serviceFeeCents: Number(request.platform_fee_amount ?? 0),
          sellerReceivesCents: Number(request.seller_amount ?? 0),
          totalCents: Number(request.amount ?? 0),
        } : null);
        setPaymentStatus(['paid', 'completed'].includes(request.status) ? 'paid' : '');
        setDone(true);
      })
      .catch(err => setError(err.response?.data?.error || text.error));
  }, [existingRequestId, text.error]);

  const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }));

  const uploadAttachments = async files => {
    const assets = [];
    for (const file of files.slice(0, 5)) {
      const uploadForm = new FormData();
      uploadForm.append('purpose', 'checkout_attachment');
      uploadForm.append('file', file);
      const upload = await api.post('/uploads', uploadForm, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      assets.push(upload.data.asset);
    }
    return assets;
  };

  const handleAttachmentChange = event => {
    const files = Array.from(event.target.files || []).slice(0, 5);
    setSelectedAttachments(files);
    event.target.value = '';
  };

  const saveRequestDetails = async () => {
    if (!requestId) return;
    setError('');
    setPaymentStatus('');
    setSubmitting(true);
    try {
      const uploaded = selectedAttachments.length ? await uploadAttachments(selectedAttachments) : [];
      const attachmentIds = uploaded.length ? uploaded.map(asset => asset.id) : savedAttachments.map(asset => asset.assetId || asset.id);
      const response = await api.put(`/payments/checkout-requests/${requestId}/details`, {
        title: form.title,
        description: form.description,
        requirements: form.requirements,
        deadline: form.deadline,
        attachmentIds,
      });
      setSavedAttachments(response.data.request?.attachments || uploaded);
      setSelectedAttachments([]);
      setPaymentStatus(text.detailsSaved);
    } catch (err) {
      setError(err.response?.data?.error || text.error);
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async e => {
    e.preventDefault();
    setError('');
    setPaymentStatus('');
    setSubmitting(true);
    try {
      const uploaded = selectedAttachments.length ? await uploadAttachments(selectedAttachments) : [];
      const res = await api.post('/payments/checkout-requests', {
        title: form.title,
        amount: Math.round(Number(form.amount) * 100),
        description: form.description,
        requirements: form.requirements,
        deadline: form.deadline,
        attachmentIds: uploaded.map(asset => asset.id),
      });
      setRequestId(res.data.id);
      setSavedAttachments(uploaded);
      setSelectedAttachments([]);
      setOrderSummary(null);
      setEscrowSplit({
        serviceFeeRate: Number(res.data.platform_fee_rate ?? ESCROW_SPLIT.serviceFeeRate),
        sellerShareRate: Number(res.data.seller_share_rate ?? ESCROW_SPLIT.sellerShareRate),
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || text.error);
    } finally {
      setSubmitting(false);
    }
  };

  const submitAlipayForm = paymentForm => {
    const holder = document.createElement('div');
    holder.style.display = 'none';
    holder.innerHTML = paymentForm;
    document.body.appendChild(holder);
    const formElement = holder.querySelector('form');
    if (!formElement) {
      holder.remove();
      throw new Error(text.alipayFormMissing);
    }
    formElement.submit();
  };

  const createAlipayPagePay = async () => {
    if (!requestId) return;
    setError('');
    setPaymentStatus(text.alipayCreating);
    setPaying(true);
    let clearStatus = true;
    try {
      const res = await api.post('/payments/alipay/create-page-pay', {
        businessType: 'escrow_checkout',
        businessId: requestId,
        currency: alipayConfig.currency || 'CNY',
        title: form.title,
        description: form.description,
      });
      if (res.data.status === 'paid') {
        setPaymentStatus('paid');
        clearStatus = false;
        return;
      }
      if (res.data.paymentForm) {
        clearStatus = false;
        setPaymentStatus(text.alipayOpened);
        submitAlipayForm(res.data.paymentForm);
        return;
      }
      setError(text.alipayError);
    } catch (err) {
      setError(err.response?.data?.error || err.message || text.alipayError);
    } finally {
      setPaying(false);
      if (clearStatus) setPaymentStatus('');
    }
  };

  const summary = (
    <aside className="account-card checkout-summary" aria-label={text.summaryTitle}>
      <h2>{text.summaryTitle}</h2>
      <div className="checkout-summary-service">
        <span>{form.title || text.servicePlaceholder}</span>
        <strong>{yuan(totalEscrowCents)}</strong>
      </div>
      <div className="checkout-summary-lines">
        <div>
          <span>{text.packageAmount}</span>
          <strong>{yuan(packageAmountCents)}</strong>
        </div>
        <div>
          <span>{text.serviceFee(escrowSplit.serviceFeeRate)}</span>
          <strong>{yuan(serviceFeeCents)}</strong>
        </div>
        <div>
          <span>{text.sellerReceives(escrowSplit.sellerShareRate)}</span>
          <strong>{yuan(sellerReceivesCents)}</strong>
        </div>
      </div>
      <div className="checkout-summary-total">
        <span>{text.totalEscrow}</span>
        <strong>{yuan(totalEscrowCents)}</strong>
      </div>
      <div className="checkout-summary-note">
        <strong>{text.beforePayTitle}</strong>
        <p>{text.beforePayText}</p>
        <p>{text.escrowNote(escrowSplit.serviceFeeRate, escrowSplit.sellerShareRate)}</p>
      </div>
      <span className="checkout-summary-audit-label">{text.serviceFeeEn(escrowSplit.serviceFeeRate)} · {text.sellerReceivesEn(escrowSplit.sellerShareRate)}</span>
    </aside>
  );

  return (
    <div className="account-page">
      <div className="container">
        <div className="account-head">
          <div>
            <h1>{text.title}</h1>
            <p>{text.subtitle}</p>
          </div>
        </div>
        <div className="checkout-layout">
          <div className="account-card checkout-main-card">
            {done ? (
              <div>
                <h2>{paymentStatus === 'paid' ? text.paidTitle : text.doneTitle}</h2>
                <p className="account-note">{paymentStatus === 'paid' ? text.paidText(displayRequestId) : text.doneText(displayRequestId)}</p>
                <div className="account-note" style={{ marginTop: 14 }}>
                  <strong>{alipayConfig.enabled === false ? text.alipayPaused : text.alipayAuto}</strong>
                  <p style={{ margin: '6px 0 0' }}>
                    {alipayConfig.enabled === false ? text.alipayNotReady : (alipayConfig.configured ? text.alipayNotice : text.alipayNotReady)}
                  </p>
                </div>
                <div className="checkout-details-editor">
                  <label>{text.serviceName}<input required value={form.title} onChange={set('title')} /></label>
                  <label>{text.requirements}<textarea required rows={5} value={form.requirements} onChange={set('requirements')} placeholder={text.requirementsPlaceholder} /></label>
                  <label>{text.deadline}<input type="date" value={form.deadline} onChange={set('deadline')} /></label>
                  <label>{text.attachments}<input type="file" multiple onChange={handleAttachmentChange} /><small>{text.attachmentsHint}</small></label>
                  {(savedAttachments.length > 0 || selectedAttachments.length > 0) && (
                    <div className="checkout-attachment-list">
                      {[...savedAttachments.map(item => ({ key: item.assetId || item.id, name: item.name })), ...selectedAttachments.map(file => ({ key: file.name, name: file.name, selected: true }))].map(item => (
                        <span key={item.key}>{item.name || item.key}{item.selected && <button type="button" onClick={() => setSelectedAttachments(files => files.filter(file => file.name !== item.name))}>{text.removeAttachment}</button>}</span>
                      ))}
                    </div>
                  )}
                  <label>{text.description}<textarea rows={3} value={form.description} onChange={set('description')} placeholder={text.descriptionPlaceholder} /></label>
                  <button className="btn btn-outline" type="button" disabled={submitting} onClick={saveRequestDetails}>{submitting ? text.savingDetails : text.saveDetails}</button>
                </div>
                {error && <div className="account-note" style={{ color: '#b91c1c', marginTop: 12 }}>{error}</div>}
                {paymentStatus && paymentStatus !== 'paid' && <div className="account-note" style={{ marginTop: 12 }}>{paymentStatus}</div>}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
                  <button className="btn btn-primary" type="button" disabled={!alipayConfig.enabled || !alipayConfig.configured || paying || paymentStatus === 'paid'} onClick={createAlipayPagePay}>
                    {paying ? text.alipayCreating : text.alipay}
                  </button>
                  <Link className="btn btn-outline" to="/wallet">{text.backWallet}</Link>
                </div>
              </div>
            ) : (
              <form className="account-form checkout-form" onSubmit={submit}>
                <label>{text.serviceName}<input required value={form.title} onChange={set('title')} placeholder={text.servicePlaceholder} /></label>
                <label>{text.amount}<input required type="number" min="1" value={form.amount} onChange={set('amount')} placeholder="3000" /></label>
                <label>{text.requirements}<textarea required rows={5} value={form.requirements} onChange={set('requirements')} placeholder={text.requirementsPlaceholder} /></label>
                <label>{text.deadline}<input type="date" value={form.deadline} onChange={set('deadline')} /></label>
                <label>{text.attachments}<input type="file" multiple onChange={handleAttachmentChange} /><small>{text.attachmentsHint}</small></label>
                {selectedAttachments.length > 0 && (
                  <div className="checkout-attachment-list">
                    {selectedAttachments.map(file => (
                      <span key={file.name}>{file.name}<button type="button" onClick={() => setSelectedAttachments(files => files.filter(item => item.name !== file.name))}>{text.removeAttachment}</button></span>
                    ))}
                  </div>
                )}
                <label>{text.description}<textarea value={form.description} onChange={set('description')} placeholder={text.descriptionPlaceholder} /></label>
                {error && <div className="account-note" style={{ color: '#b91c1c' }}>{error}</div>}
                <button className="btn btn-primary" type="submit" disabled={submitting}>{submitting ? text.submitting : text.submit}</button>
              </form>
            )}
          </div>
          {summary}
        </div>
      </div>
    </div>
  );
}
